import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
} from '@roger/ui-components';
import { Clipboard, Loader2 } from 'lucide-react';

import { BespokeLayoutRenderer } from '../../../components/oasis/bespoke/layout/BespokeLayoutRenderer';
import type { LayoutNode } from '../../../components/oasis/bespoke/layout/types';
import type { Question } from '../../../components/oasis/types';
import type {
  BespokeTemplateResponse,
  FormSchemaVersionPin,
  RogerDocFormTypeCatalogEntry,
} from '../../../services/api/types.gen';
import {
  type SectionPortCandidate,
  type SectionPortCandidateGroup,
  countGroupMembersMatchingLayout,
} from './sectionLayoutPortShared';
import {
  type SectionLayoutPortDialogToast,
  useSectionLayoutPortDialog,
} from './useSectionLayoutPortDialog';

export type { SectionLayoutPortDialogToast };

export type SectionLayoutPortDialogProps = {
  open: boolean;
  onClose: () => void;
  sourceLayout: LayoutNode[];
  /** Full RogerDoc pin for the source template — names alone are not unique. */
  sourcePin: FormSchemaVersionPin;
  sourceSectionName: string;
  sourceSectionSchema: Question[];
  formTypeCatalog: RogerDocFormTypeCatalogEntry[];
  toast: SectionLayoutPortDialogToast;
  /** Called with the rows that were actually saved (for local state patching). */
  onApplied?: (saved: BespokeTemplateResponse[]) => void;
};

function upToDateBadgeLabel(group: SectionPortCandidateGroup): string | null {
  const count = group.members.filter(
    m => m.portResultMatchesExistingLayout
  ).length;
  if (count === 0) return null;
  if (count === group.members.length) {
    return 'Already up to date';
  }
  return `Already up to date (${count}/${group.members.length})`;
}

function formatQidOverlapSummary(rep: SectionPortCandidate): string {
  const parts = [`${rep.sharedQids.length} QIDs shared`];
  if (rep.destOnlyQids.length > 0) {
    parts.push(`${rep.destOnlyQids.length} dest-only`);
  }
  if (rep.sourceOnlyQids.length > 0) {
    parts.push(`${rep.sourceOnlyQids.length} source-only`);
  }
  return parts.join(' · ');
}

export function SectionLayoutPortDialog({
  open,
  onClose,
  sourceLayout,
  sourcePin,
  sourceSectionName,
  sourceSectionSchema,
  formTypeCatalog,
  toast,
  onApplied,
}: SectionLayoutPortDialogProps) {
  const {
    loading,
    error,
    failedVisitTypeLoads,
    discovery,
    groupState,
    copying,
    previewGroupKey,
    setPreviewGroupKey,
    allowInvalidOverride,
    setAllowInvalidOverride,
    runDiscovery,
    exactSchemaMatchGroups,
    partialOverlapGroups,
    exactSchemaMatchWorkCount,
    partialOverlapWorkCount,
    needsWorkCount,
    allBoundGroupsBySignature,
    alreadyMatch,
    handleCopySelectedGroup,
    scanned,
    discoveryIncomplete,
    busy,
    previewGroup,
    selectedDestCount,
    previewHasValidationWarning,
    handleCopyDebugBundle,
  } = useSectionLayoutPortDialog({
    open,
    sourceLayout,
    sourcePin,
    sourceSectionName,
    sourceSectionSchema,
    formTypeCatalog,
    toast,
    onApplied,
  });

  const renderGroupRow = (
    group: SectionPortCandidateGroup,
    options: { showExactBadge?: boolean } = {}
  ) => {
    const { showExactBadge = false } = options;
    const sig = group.destSchemaSignature;
    const rep = group.representative;
    const st = groupState[sig] ?? {};
    const isPreviewFocused = previewGroupKey === sig;
    const memberErrorEntries = Object.entries(st.memberErrors ?? {});
    // Look up the group's full (unfiltered) membership for the "already up
    // to date" count — `group` here only contains members still needing a
    // copy, so it can never itself report any as already matching.
    const fullGroup = allBoundGroupsBySignature.get(sig) ?? group;
    const upToDateLabel = upToDateBadgeLabel(fullGroup);
    const hasValidationWarning = group.members.some(m => m.validationWarning);

    return (
      <li
        key={sig}
        role='button'
        tabIndex={0}
        aria-selected={isPreviewFocused}
        className={`cursor-pointer rounded border text-sm transition-colors ${
          isPreviewFocused
            ? 'border-primary bg-primary/5 ring-1 ring-primary'
            : 'hover:bg-muted/40'
        }`}
        onClick={() => {
          setPreviewGroupKey(sig);
          setAllowInvalidOverride(false);
        }}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setPreviewGroupKey(sig);
            setAllowInvalidOverride(false);
          }
        }}
      >
        <div className='p-2'>
          <div className='min-w-0 space-y-1'>
            <div className='flex flex-wrap items-center gap-2'>
              <span className='min-w-0 flex-1 truncate font-medium'>
                {group.members.length === 1
                  ? rep.displayLabel
                  : `${group.members.length} destinations · same schema`}
              </span>
              {group.members.length > 1 && (
                <Badge variant='outline'>
                  {group.members.length} destinations
                </Badge>
              )}
              {showExactBadge && (
                <Badge variant='secondary'>Exact schema match</Badge>
              )}
              {hasValidationWarning && (
                <Badge variant='outline'>Invalid destination layout</Badge>
              )}
              {upToDateLabel && (
                <Badge variant='secondary'>{upToDateLabel}</Badge>
              )}
              {st.copying && (
                <Loader2 className='h-3.5 w-3.5 animate-spin text-muted-foreground' />
              )}
            </div>
            {group.members.length > 1 && (
              <ul className='text-[11px] text-muted-foreground'>
                {/*
                 * No per-member "· warn" tag here: bindGroupToSharedPreviewLayout
                 * copies the representative's validationWarning onto every
                 * member, so a per-row tag would always be all-or-nothing for
                 * the whole group, never pointing at a specific destination.
                 * The group-level "Invalid destination layout" badge above
                 * already covers this.
                 */}
                {group.members.map(m => (
                  <li key={m.template.id} className='truncate'>
                    {m.displayLabel}
                    {st.memberErrors?.[m.template.id] ? ' · failed' : ''}
                  </li>
                ))}
              </ul>
            )}
            <p className='text-[11px] text-muted-foreground'>
              {formatQidOverlapSummary(rep)}
            </p>
            {memberErrorEntries.map(([id, msg]) => {
              const label =
                group.members.find(m => m.template.id === id)?.displayLabel ??
                id;
              return (
                <p key={id} className='text-[11px] text-destructive'>
                  {label}: {msg}
                </p>
              );
            })}
          </div>
        </div>
      </li>
    );
  };

  const renderPreviewPanel = () => {
    if (!previewGroup) {
      return (
        <div className='flex h-full flex-col items-center justify-center p-8 text-center text-sm text-muted-foreground'>
          <p>Select a destination group to preview the spliced layout.</p>
        </div>
      );
    }

    const rep = previewGroup.representative;
    const previewLayout = rep.resultLayout;
    // Same reasoning as renderGroupRow: `previewGroup` only contains members
    // still needing a copy, so match the full (unfiltered) group instead to
    // report any siblings that already have the shared layout.
    const fullPreviewGroup =
      allBoundGroupsBySignature.get(previewGroup.destSchemaSignature) ??
      previewGroup;
    const matchCount = countGroupMembersMatchingLayout(fullPreviewGroup);
    const matchesLabel =
      matchCount === 0
        ? null
        : matchCount === fullPreviewGroup.members.length
          ? 'Matches current layout'
          : `Matches current layout (${matchCount}/${fullPreviewGroup.members.length})`;
    const validationWarning =
      previewGroup.members.find(m => m.validationWarning)?.validationWarning ??
      rep.validationWarning;

    return (
      <div className='flex h-full min-h-0 flex-col'>
        <div className='shrink-0 space-y-2 border-b px-4 py-3'>
          <p className='text-sm font-medium'>
            {previewGroup.members.length === 1
              ? rep.displayLabel
              : `${previewGroup.members.length} destinations · same schema`}
          </p>
          <p className='text-xs text-muted-foreground'>
            Deterministic splice preview — Copy saves this layout to every
            destination in the group
            {matchesLabel ? ` · ${matchesLabel}` : ''}
          </p>
          {validationWarning && (
            <p className='text-xs text-amber-800 dark:text-amber-400'>
              Validation: {validationWarning}
            </p>
          )}
        </div>
        <div className='min-h-0 flex-1 overflow-auto p-4'>
          {previewLayout.length === 0 ? (
            <p className='text-sm text-muted-foreground'>
              Splice produced no layout nodes.
            </p>
          ) : (
            <div className='pointer-events-none w-max min-w-full'>
              <BespokeLayoutRenderer
                layout={previewLayout}
                answers={{}}
                onAnswerChange={() => {}}
                onFormDataChange={() => {}}
                schema={rep.destQuestions as Question[]}
                schemaQuestionOptions={[]}
                sectionName={rep.sectionName}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderAlreadyMatchRow = (c: SectionPortCandidate) => (
    <li key={c.template.id} className='rounded border p-2 text-sm'>
      <span className='font-medium'>{c.displayLabel}</span>
      <Badge variant='secondary' className='ml-2'>
        {c.layoutAlreadyMatches
          ? 'Already matches source'
          : 'Already up to date'}
      </Badge>
    </li>
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(next: boolean) => {
        // Keep the dialog open while a copy is in flight so dismiss can't
        // look like cancel while saves continue in the background.
        if (!next && copying) return;
        if (!next) onClose();
      }}
    >
      <DialogContent
        className='flex h-[92vh] max-h-[92vh] w-[96vw] max-w-[96vw] flex-col gap-0 overflow-hidden p-0 sm:rounded-lg'
        onPointerDownOutside={event => {
          if (copying) event.preventDefault();
        }}
        onEscapeKeyDown={event => {
          if (copying) event.preventDefault();
        }}
      >
        <DialogHeader className='shrink-0 border-b px-6 py-4'>
          <DialogTitle>Copy to similar sections</DialogTitle>
          <DialogDescription>
            Deterministically splice shared layout blocks from this section into
            other live forms.
          </DialogDescription>
        </DialogHeader>

        <div className='flex min-h-0 flex-1 flex-col overflow-hidden'>
          {loading ? (
            <div className='flex flex-1 items-center justify-center gap-2 text-sm text-muted-foreground'>
              <Loader2 className='h-5 w-5 animate-spin' />
              Scanning live sections…
            </div>
          ) : error ? (
            <div className='flex-1 overflow-auto p-6'>
              <div className='rounded border border-destructive bg-destructive/5 p-3 text-sm text-destructive'>
                {error}
              </div>
            </div>
          ) : scanned ? (
            <div className='flex min-h-0 flex-1 overflow-hidden'>
              <div className='flex w-[min(100%,28rem)] shrink-0 flex-col border-r md:w-[38%]'>
                <div className='min-h-0 flex-1 space-y-5 overflow-y-auto p-4'>
                  {discoveryIncomplete && (
                    <div
                      role='alert'
                      className='rounded border border-destructive/60 bg-destructive/10 p-3 text-sm text-destructive'
                    >
                      <p className='font-medium'>Discovery incomplete</p>
                      <p className='mt-1 text-destructive/90'>
                        Some form types or schema versions could not be loaded,
                        so destinations may be missing from this list. Re-scan
                        until this clears before copying.
                      </p>
                    </div>
                  )}
                  {exactSchemaMatchGroups.length > 0 && (
                    <section className='space-y-2'>
                      <h3 className='text-sm font-semibold'>
                        Exact schema match ({exactSchemaMatchWorkCount}{' '}
                        destination
                        {exactSchemaMatchWorkCount === 1 ? '' : 's'},{' '}
                        {exactSchemaMatchGroups.length} group
                        {exactSchemaMatchGroups.length === 1 ? '' : 's'})
                      </h3>
                      <p className='text-xs text-muted-foreground'>
                        Same question set as the source. Still uses the
                        deterministic splice.
                      </p>
                      <ul className='space-y-2'>
                        {exactSchemaMatchGroups.map(g =>
                          renderGroupRow(g, { showExactBadge: true })
                        )}
                      </ul>
                    </section>
                  )}

                  <section className='space-y-2'>
                    <h3 className='text-sm font-semibold'>
                      Partial overlap ({partialOverlapWorkCount} destination
                      {partialOverlapWorkCount === 1 ? '' : 's'},{' '}
                      {partialOverlapGroups.length} group
                      {partialOverlapGroups.length === 1 ? '' : 's'})
                    </h3>
                    {partialOverlapGroups.length === 0 ? (
                      <p className='text-sm text-muted-foreground'>
                        {needsWorkCount === 0
                          ? 'No other live sections need a splice (or all matches are already up to date).'
                          : 'All pending destinations have an exact schema match.'}
                      </p>
                    ) : (
                      <ul className='space-y-2'>
                        {partialOverlapGroups.map(g => renderGroupRow(g))}
                      </ul>
                    )}
                  </section>

                  {alreadyMatch.length > 0 && (
                    <section className='space-y-2'>
                      <h3 className='text-sm font-semibold'>
                        Already up to date ({alreadyMatch.length})
                      </h3>
                      <p className='text-xs text-muted-foreground'>
                        Identical source layout or splice would not change the
                        destination — shown for audit only.
                      </p>
                      <ul className='space-y-2'>
                        {alreadyMatch.map(c => renderAlreadyMatchRow(c))}
                      </ul>
                    </section>
                  )}

                  {discoveryIncomplete && failedVisitTypeLoads.length > 0 && (
                    <p className='text-xs text-muted-foreground'>
                      Failed form types: {failedVisitTypeLoads.join(', ')}
                    </p>
                  )}
                  {discoveryIncomplete &&
                    discovery &&
                    discovery.unresolvedVersionNames.length > 0 && (
                      <p className='text-xs text-muted-foreground'>
                        Unresolved pins:{' '}
                        {discovery.unresolvedVersionNames.join(', ')}
                      </p>
                    )}
                </div>
              </div>

              <div className='flex min-h-0 min-w-0 flex-1 flex-col bg-muted/20'>
                <div className='flex shrink-0 items-center justify-between gap-2 border-b px-4 py-2'>
                  <h3 className='text-sm font-semibold'>Layout preview</h3>
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    className='shrink-0 font-normal text-muted-foreground'
                    disabled={!previewGroup || busy}
                    onClick={() => void handleCopyDebugBundle()}
                  >
                    <Clipboard className='h-3.5 w-3.5' aria-hidden />
                    Copy debug bundle
                  </Button>
                </div>
                <div className='min-h-0 flex-1'>{renderPreviewPanel()}</div>
              </div>
            </div>
          ) : null}
        </div>

        <DialogFooter className='shrink-0 flex-col items-stretch gap-3 border-t px-6 py-4 sm:flex-col'>
          {scanned && previewHasValidationWarning && (
            <div className='flex items-start gap-2'>
              <Checkbox
                id='section-port-allow-invalid'
                checked={allowInvalidOverride}
                onCheckedChange={checked =>
                  setAllowInvalidOverride(checked === true)
                }
                disabled={busy}
                className='mt-0.5'
              />
              <Label
                htmlFor='section-port-allow-invalid'
                className='cursor-pointer text-xs font-normal text-amber-800 dark:text-amber-400'
              >
                Copy anyway — the preview layout failed validation and may break
                the destination form.
              </Label>
            </div>
          )}
          {scanned && (
            <div className='flex flex-wrap justify-end gap-2'>
              <Button
                variant='outline'
                onClick={() => void runDiscovery()}
                disabled={busy}
              >
                Re-scan
              </Button>
              <Button
                onClick={() => void handleCopySelectedGroup()}
                disabled={
                  busy ||
                  discoveryIncomplete ||
                  selectedDestCount === 0 ||
                  !previewGroupKey ||
                  (previewHasValidationWarning && !allowInvalidOverride)
                }
              >
                {copying && <Loader2 className='mr-1 h-4 w-4 animate-spin' />}
                Copy
                {selectedDestCount > 0
                  ? ` · ${selectedDestCount} destination${
                      selectedDestCount === 1 ? '' : 's'
                    }`
                  : ''}
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
